import React, { forwardRef, useId } from 'react';
import { clsx } from 'clsx';
import type { InputProps } from '@/types/ui';

/**
 * Webcoda-styled Input component
 * Supports various input types, validation states, and accessibility features
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(({
  type = 'text',
  value,
  defaultValue,
  placeholder,
  disabled = false,
  required = false,
  error,
  success = false,
  helperText,
  label,
  name,
  id,
  onChange,
  onBlur,
  onFocus,
  leftIcon,
  rightIcon,
  autoComplete,
  autoFocus = false,
  className,
  'data-testid': testId,
  ...props
}, ref) => {
  // Use controlled or uncontrolled pattern
  const isControlled = value !== undefined;

  const generatedId = useId();
  const inputId = id || name || generatedId;
  const hasError = Boolean(error);
  const hasSuccess = success && !hasError;

  // Base input classes
  const baseClasses = [
    'block',
    'w-full',
    'rounded-lg',
    'border',
    'bg-white',
    'px-3',
    'py-2',
    'text-sm',
    'placeholder-gray-400',
    'shadow-soft',
    'transition-all',
    'duration-200',
    'focus:outline-none',
    'focus:ring-1',
    'disabled:bg-gray-50',
    'disabled:cursor-not-allowed',
    'disabled:opacity-50',
  ];

  // State-based classes
  const stateClasses = hasError
    ? ['border-error-500', 'focus:border-error-500', 'focus:ring-error-500']
    : hasSuccess
    ? ['border-success-500', 'focus:border-success-500', 'focus:ring-success-500']
    : ['border-gray-300', 'focus:border-primary-500', 'focus:ring-primary-500'];

  // Icon padding adjustments
  const paddingClasses = [
    leftIcon ? 'pl-10' : 'pl-3',
    rightIcon ? 'pr-10' : 'pr-3',
  ];

  const inputClasses = clsx(
    baseClasses,
    stateClasses,
    paddingClasses,
    className
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e);
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    onBlur?.(e);
  };

  return (
    <div className="space-y-2">
      {/* Label */}
      {label && (
        <label 
          htmlFor={inputId}
          className={clsx(
            'block text-sm font-medium',
            hasError ? 'text-error-700' : hasSuccess ? 'text-success-700' : 'text-gray-700'
          )}
        >
          {label}
          {required && (
            <span className="ml-1 text-error-500" aria-label="required">
              *
            </span>
          )}
        </label>
      )}

      {/* Input Container */}
      <div className="relative">
        {/* Left Icon */}
        {leftIcon && (
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <span 
              className={clsx(
                'text-sm',
                hasError ? 'text-error-400' : hasSuccess ? 'text-success-400' : 'text-gray-400'
              )}
              aria-hidden="true"
            >
              {leftIcon}
            </span>
          </div>
        )}

        {/* Input Field */}
        <input
          ref={ref}
          id={inputId}
          name={name}
          type={type}
          {...(isControlled ? { value } : { defaultValue })}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          className={inputClasses}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          data-testid={testId}
          aria-invalid={hasError}
          aria-describedby={
            clsx([
              error && `${inputId}-error`,
              helperText && `${inputId}-help`,
            ].filter(Boolean).join(' ')) || undefined
          }
          {...props}
        />

        {/* Right Icon */}
        {rightIcon && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <span 
              className={clsx(
                'text-sm',
                hasError ? 'text-error-400' : hasSuccess ? 'text-success-400' : 'text-gray-400'
              )}
              aria-hidden="true"
            >
              {rightIcon}
            </span>
          </div>
        )}
      </div>

      {/* Helper Text / Error Message */}
      {(error || helperText) && (
        <div className="flex items-start space-x-1">
          {hasError && (
            <svg 
              className="h-4 w-4 mt-0.5 text-error-500 flex-shrink-0" 
              fill="currentColor" 
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path 
                fillRule="evenodd" 
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" 
                clipRule="evenodd" 
              />
            </svg>
          )}
          
          {hasSuccess && (
            <svg 
              className="h-4 w-4 mt-0.5 text-success-500 flex-shrink-0" 
              fill="currentColor" 
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path 
                fillRule="evenodd" 
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" 
                clipRule="evenodd" 
              />
            </svg>
          )}
          
          <p
            id={error ? `${inputId}-error` : `${inputId}-help`}
            className={clsx(
              'text-sm',
              hasError ? 'text-error-600' : hasSuccess ? 'text-success-600' : 'text-gray-500'
            )}
          >
            {error || helperText}
          </p>
        </div>
      )}
    </div>
  );
});

Input.displayName = 'Input';